import { isString } from 'lodash-es';

import { CharacterEnum } from '../enums/character.enum';
import { BBOData, MarketState, MarketStateInstrumentSnapshot, OHLCV, SymbolStatus, TradeData } from '../models/market-data/types';
import { QueriesCacheType, QueryCacheItemType } from '../models/trading/types';
import { BBODataCache, InstrumentSnapshotData, InstrumentSnapshotMap, SymbolLite, SymbolStatusDataCache, TradeDataCache } from '../store/market-data/types';
import MarketStateCache from '../store-util/MarketStateCache';

import { calculateMarketStateFromDate } from './DateTimeHelpers';
import { NullableString } from './types';


/**
 * Used by MarketData.service only. This is a wrapper of `processMDQueryData`
 * @param dispatch The dispatch function to trigger actions
 * @param response The response from the service
 * @param ohlcvQueriesCache The cache data, containing symbol and queryCase
 */
export function processOHLCVResponse(dispatch: any, response: any, ohlcvQueriesCache: QueriesCacheType) {
  const queryCache: QueryCacheItemType = (ohlcvQueriesCache as any)[response.ref] || {};

  processMDQueryData(dispatch, queryCache, response);
}

/**
 * // TODO: Currently the apps are not using OHLCV data from here in favor of MarketDataSummary calls
 *          Must not be deleted though as we might need this data in the future
 *          Therefore leaving commented out switch to remind how it was initially used
 * Used by MarketData.service
 * @param dispatch The dispatch function to trigger actions
 * @param queryCache The cache that contains queryCase and symbol
 * @param response The response from the service
 */
export function processMDQueryData(dispatch: any, queryCache: QueryCacheItemType, response: any) {
  const { queryCase, symbol } = queryCache;
  const ohlcvData: OHLCV[] = (response?.data || response) as OHLCV[];

  /*
  switch (queryCase) {
    case GWQueryCase.OhlcvWatchlistItemRecentPrices:
      // last 30 'close' prices, per one minute (30 items or less)

      if (ohlcvData?.length > 0) {
        const prices = (
          ohlcvData
            .slice(Math.max(response.length - 30, 0))
            .map(ohlcv => ohlcv.close)
        );
        dispatch(watchListItemRecentPricesLoaded({ symbol, prices }));
      }
      break;

    default:
      console.warn(
        `[MarketDataHelpers] Unknown query case '${GWQueryCase[queryCase as GWQueryCase]}' for ref=${response.ref}`,
        response,
      );
      break;
  }
  */
}

/**
 * NB: Meant to be used through MarketDataCache. Use directly only if check is needed
 *     for market state from time and date different from now.
 * For reference of Market State times - see:
 *  (1) Google Presentation - https://docs.google.com/presentation/d/17j2erZF0NBqA67IF71b8ub0JK5p7-__OPhYCpyMRd00/edit#slide=id.g10947438435_0_36
 *  (2) Library constants at `capital-library/constants/marketHours.ts`
 */
export function calculateMarketState(customDateInUtc?: NullableString) {
  return calculateMarketStateFromDate(customDateInUtc);
}

export function convertToMarketState(value: MarketStateInstrumentSnapshot | null) {
  switch (value) {
    case MarketStateInstrumentSnapshot.IS_PreMarket: return MarketState.PreMarket;
    case MarketStateInstrumentSnapshot.IS_RegularHours: return MarketState.RegularHours;
    case MarketStateInstrumentSnapshot.IS_AfterMarket: return MarketState.AfterMarket;
    case MarketStateInstrumentSnapshot.IS_MarketClosed:
    case MarketStateInstrumentSnapshot.IS_MarketClosedNextDay:
      return MarketState.MarketClosed;
    default: return null;
  }
}

export const dataArrayToMap = (data: (TradeData | BBOData | InstrumentSnapshotData | SymbolStatus)[]) => {
  const result: TradeDataCache | BBODataCache | InstrumentSnapshotMap | SymbolStatusDataCache = {};
  const count = data.length;
  for (let i = 0; i < count; i++) {
    const item = data[i];
    result[item.sym] = item;
  }

  return result;
};

/**
 * If `marketState` is not given as an argument or is `null` - it is retrieved from cache
 */
export function isMarketClosed(marketState?: MarketStateInstrumentSnapshot | MarketState | null) {
  let theState = marketState ?? MarketStateCache.get().marketState;

  return [
    MarketState.MarketClosed,
    MarketStateInstrumentSnapshot.IS_MarketClosedNextDay,
    MarketStateInstrumentSnapshot.IS_MarketClosed,
  ].includes(theState as MarketStateInstrumentSnapshot | MarketState);
}

export function isMarketRegularHours(marketState?: MarketStateInstrumentSnapshot | MarketState | null) {
  let theState = marketState ?? MarketStateCache.get().marketState;

  // eslint-disable-next-line max-len
  return ([ MarketState.RegularHours, MarketStateInstrumentSnapshot.IS_RegularHours ]).includes(theState as MarketStateInstrumentSnapshot | MarketState);
}

export function isMarketOpen(marketState?: MarketStateInstrumentSnapshot | MarketState | null) {
  let theState = marketState ?? MarketStateCache.get().marketState;

  return ([
    MarketState.RegularHours,
    MarketStateInstrumentSnapshot.IS_RegularHours,
    MarketState.PreMarket,
    MarketStateInstrumentSnapshot.IS_PreMarket,
    MarketState.AfterMarket,
    MarketStateInstrumentSnapshot.IS_AfterMarket,
  ].includes(theState as MarketStateInstrumentSnapshot | MarketState)
  );
}

/**
 * @param inputItems A list of objects with `symbol` property
 * @param asString Default is `true`. If `false` - return as array of strings
 * @param withCount If `true` - return string is prepended with '(<count>)' for example '(2) AAPL,TSLA'
 * @param noSort If `true` - sorting is skipped and result is returned as is
 * @returns A comma separated list of strings or array of strings - depending on `asString` value
 */
function extractSymbolsFromList(
  inputItems: SymbolLite[] | string[],
  asString: boolean,
  withCount?: boolean,
  noSort?: boolean,
) {
  const asArray = isString(inputItems) ? inputItems?.split(',') : inputItems;
  const firstElement = (asArray?.[0] as any);
  if (!asArray || !firstElement) return asString ? (withCount ? '(0)' : '') : []; // eslint-disable-line no-nested-ternary

  let symbolsArray: string[] = [];
  symbolsArray = (
    (!firstElement?.symbol && isString(firstElement))
      ? asArray as string[]
      : (asArray as SymbolLite[]).map(({ symbol }) => symbol)
  );
  if (!noSort) symbolsArray = [ ...symbolsArray ].sort();
  if (!asString) return symbolsArray;

  return (withCount ? `(${symbolsArray.length}) ` : '') + symbolsArray.join();
}

/**
 * @param inputItems A list of objects with `symbol` property
 * @param withCount If `true` - return string is prepended with '(<count>)' - for example '(2) AAPL,TSLA'
 * @param noSort If `true` - sorting is skipped and result is returned as is
 * @returns A comma separated string - for example 'C,NVDA,AMD'
 */
export function extractSymbolsFromListAsString(
  inputItems: SymbolLite[] | string[],
  withCount?: boolean,
  noSort?: boolean,
) {
  return extractSymbolsFromList(inputItems, true, withCount, noSort) as string;
}

/**
 * @param inputItems A list of objects with `symbol` property
 * @param noSort If `true` - sorting is skipped and result is returned as is
 * @returns An array of symbols - for example `['C', 'NVDA', 'AMD']`
 */
export function extractSymbolsFromListAsArray(inputItems: SymbolLite[] | string[], noSort?: boolean) {
  return extractSymbolsFromList(inputItems, false, false, noSort) as string[];
}

export const changeResponseSymbolDataConversion = (symbol: string): string => {
  // This is needed for users that are not verified
  if (symbol.includes('.V')) {
    return symbol.replace('.V', '');
  }
  // This is needed for symbols with a special character (CQS => NASDAQ conversion) so we can receive and manage market data updates
  if (symbol.includes('/')) {
    return symbol.replace('/', '.');
  }

  return symbol;
};

export const getSymbolFirstPart = (symbol: NullableString): string => {
  if (!symbol) {
    return '';
  }

  return symbol.split(CharacterEnum.Slash)[0];
};

export const changeRequestSymbolDataConversion = (symbol: string): string => (
  // In order to receive and manage market udpates we need to apply conversion to symbols with special character
  symbol.includes('.') ? symbol.replace('.', '/') : getSymbolFirstPart(symbol)
);

export const subscribeSymbolsWithDataConversion = (symbolList: string[]): string[] => (
  symbolList.map(symbol => changeRequestSymbolDataConversion(symbol))
);
